import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts'
import { type IFiltersListGroups } from 'entities/Contacts/api/types'
import { SurveysApi } from 'entities/Surveys'

type ISurveyEnrolmentStoreParams = {
  data: {
    name: string
    filters: IFiltersListGroups | null
    exclude_filters: IFiltersListGroups | null
    id: number
    isPublished?: boolean
  }
  onNext?: () => void
}

const isEmptyFilters = (filters: IFiltersListGroups | null): filters is null => {
  if (!filters) return true
  return (
    !filters.advanced?.length &&
    !filters.segments?.length &&
    !filters.contacts?.length &&
    !filters.tags?.length
  )
}

export class SurveyEnrolmentStore {
  constructor() {
    makeAutoObservable(this)
  }
  modalId = nanoid()

  init = async (params: ISurveyEnrolmentStoreParams): Promise<void> => {
    const { filters, exclude_filters } = params.data
    if (isEmptyFilters(filters) && isEmptyFilters(exclude_filters)) {
      this._showActivatedToast(params.data.isPublished)
      return params.onNext?.()
    }
    try {
      const { data } = await ContactsApi.getContactsFiltersCount({
        filtersListGroups: filters ?? undefined,
        excludeFiltersListGroups: exclude_filters ?? undefined,
      })
      if (data.count) {
        return new Promise((resolve) => {
          this._openModal({ ...params, enrolled_contacts_count: data.count, resolve })
        })
      } else {
        this._showActivatedToast(params.data.isPublished)
        params.onNext?.()
      }
    } catch (e) {
      logger.error(e)
      const message = e instanceof AxiosError ? e.errors.getError('message') : null
      showToast({
        type: 'error',
        title: message || 'Unable to check matching contacts. Please try again.',
      })
      this._showActivatedToast(params.data.isPublished)
      params.onNext?.()
    }
  }

  private _showActivatedToast = (isPublished?: boolean, enrolledCount?: number) => {
    const baseTitle = isPublished ? 'Survey published' : 'Survey activated'
    const contactWord = enrolledCount === 1 ? 'contact' : 'contacts'
    const title = enrolledCount
      ? `${baseTitle}. ${enrolledCount} ${contactWord} enrolled.`
      : baseTitle

    showToast({ type: 'success', title })
  }

  private _onClose = (onNext?: () => void, resolve?: () => void) => {
    modalStore.closeModal(this.modalId)
    onNext?.()
    resolve?.()
  }

  private _openModal({
    data: { name, id, isPublished },
    onNext,
    enrolled_contacts_count,
    resolve,
  }: ISurveyEnrolmentStoreParams & { enrolled_contacts_count: number; resolve: () => void }) {
    const contactWord = enrolled_contacts_count === 1 ? 'contact' : 'contacts'
    const matchWord = enrolled_contacts_count === 1 ? 'contact matches' : 'contacts match'

    modalStore.addModal({
      id: this.modalId,
      type: ModalTypeList.SUCCESS,
      disabledOnAllClose: true,
      disabledOnEscClose: true,
      disabledOverlayClose: true,
      title: isPublished ? 'Survey published' : 'Survey activated',
      subtitle: `"${name}"`,
      desc: `${enrolled_contacts_count} ${matchWord} your survey's filters. Would you like to enroll them now?`,
      primaryAction: {
        text: `Yes, enroll ${enrolled_contacts_count} ${contactWord}`,
        onAction: async () => {
          try {
            await SurveysApi.enrolmentSurvey({
              id,
              data: { apply_filters: false, apply_to_all: true },
            })
            this._showActivatedToast(isPublished, enrolled_contacts_count)
          } catch (e) {
            logger.error(e)
            const message = e instanceof AxiosError ? e.errors.getError('message') : null
            showToast({ type: 'error', title: message || 'Unable to enroll contacts' })
          } finally {
            this._onClose(onNext, resolve)
          }
        },
      },
      secondaryAction: {
        text: 'No, don’t enroll',
        onAction: () => {
          this._showActivatedToast(isPublished)
          this._onClose(onNext, resolve)
        },
      },
    })
  }
}
